import { useQueryClient } from "@tanstack/react-query";
import { Ban, Download, Paperclip, RefreshCw } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, jsonBody } from "../../api/client";
import type { EmailMessage } from "../../api/types";
import { formatDateTime, initials } from "../../lib/format";
import {
  Card,
  CardHeader,
  ConfirmDialog,
  EditorPage,
  EmptyState,
  ErrorPanel,
  FormActions,
  StatusPill,
} from "../../ui/components";
import { DetailPageSkeleton, SkeletonText } from "../../ui/skeletons";
import {
  emptyDraft,
  type MessageDraft,
  MessageFields,
  scheduleToISO,
  templatesForPurpose,
} from "./MessageFields";
import { normalizeSubject, splitQuoted } from "./messageText";
import {
  attachmentDownloadUrl,
  formatFileSize,
  messageStatusLabels,
  threadPurposeLabels,
  useEmailTemplates,
  useEmailThread,
  useEmailThreadMessages,
} from "./queries";

// Closing an exchange is not deleting it. The messages stay, because what was said to a customer
// is part of the record of their booking, and the next colleague to open their record needs to
// be able to read it.
export function ThreadPage() {
  const { threadId } = useParams<{ threadId: string }>();
  const queryClient = useQueryClient();
  const threadQuery = useEmailThread(threadId);
  const messages = useEmailThreadMessages(threadId);
  const templates = useEmailTemplates();
  const [draft, setDraft] = useState<MessageDraft>(emptyDraft);
  const [cancelling, setCancelling] = useState<EmailMessage | null>(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();
  const thread = threadQuery.data;

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ["messaging", "threads"] });
  }

  async function reply(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!thread) return;
    setWorking(true);
    setError(undefined);
    try {
      await api(`/api/v1/messaging/threads/${thread.id}/messages`, {
        method: "POST",
        body: jsonBody({
          // A reply goes to the customer this exchange is with, and the server reads that off
          // the thread. It used to carry a recipient list rebuilt from the messages already
          // sent, because a thread could be opened against an account rather than a person and
          // then had no recorded audience of its own.
          purpose: thread.purpose,
          templateKey: draft.templateKey,
          subject: draft.templateKey ? "" : draft.subject,
          textBody: draft.templateKey ? "" : draft.textBody,
          scheduledAt: scheduleToISO(draft.scheduledAt),
        }),
      });
      setDraft(emptyDraft);
      await refresh();
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function setStatus(status: string) {
    if (!thread) return;
    setWorking(true);
    setError(undefined);
    try {
      await api(`/api/v1/messaging/threads/${thread.id}/status`, {
        method: "PUT",
        body: jsonBody({ status, version: thread.version }),
      });
      await refresh();
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function cancelScheduled() {
    if (!thread || !cancelling) return;
    setWorking(true);
    setError(undefined);
    try {
      await api(`/api/v1/messaging/threads/${thread.id}/messages/${cancelling.id}`, {
        method: "DELETE",
      });
      setCancelling(null);
      await refresh();
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  // Retrying edits the delivery that failed rather than writing a new one, so the conversation
  // keeps a single row for a message the customer was only ever owed once.
  //
  // No confirmation step, unlike cancelling. Stopping a send destroys an intention somebody had;
  // repeating one that demonstrably did not arrive restores it, and putting a dialog in front of
  // that would only be ceremony.
  //
  // The server puts the row back to queued, which is one of the states useEmailThreadMessages
  // polls on, so the outcome lands here on its own rather than waiting for somebody to reload.
  async function retrySend(message: EmailMessage) {
    if (!thread) return;
    setWorking(true);
    setError(undefined);
    try {
      await api(`/api/v1/messaging/threads/${thread.id}/messages/${message.id}/retry`, {
        method: "POST",
      });
      await refresh();
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  if (threadQuery.isLoading)
    return <DetailPageSkeleton title="Conversation" backTo="/emails" backLabel="Back to emails" />;
  if (threadQuery.error) return <ErrorPanel error={threadQuery.error} />;
  if (!thread) {
    return (
      <EditorPage
        title="Conversation not found"
        backTo="/emails"
        backLabel="Back to emails"
        description="The conversation is outside your current access scope or no longer exists."
      >
        <EmptyState
          title="Conversation not found"
          description="Choose another conversation from the mailbox."
        />
      </EditorPage>
    );
  }

  const available = templatesForPurpose(templates.data?.items ?? [], thread.purpose);

  return (
    <EditorPage
      title={thread.subject}
      description={`${threadPurposeLabels[thread.purpose] ?? thread.purpose} conversation with ${thread.customerName}.`}
      backTo="/emails"
      backLabel="Back to emails"
    >
      {error ? <ErrorPanel error={error} /> : null}
      <div className="stack">
        <Card>
          <CardHeader
            title="Conversation"
            description="Newest last. Messages waiting to be sent are shown here too, so nobody writes the same thing twice."
            actions={
              <div className="inline">
                <StatusPill value={thread.status} />
                <Link className="button secondary small" to={`/contacts/${thread.customerId}`}>
                  {thread.customerName}
                </Link>
                <button
                  className="button secondary small"
                  type="button"
                  disabled={working}
                  onClick={() => setStatus(thread.status === "open" ? "closed" : "open")}
                >
                  {thread.status === "open" ? "Close conversation" : "Reopen conversation"}
                </button>
              </div>
            }
          />
          {messages.error ? (
            <ErrorPanel error={messages.error} />
          ) : messages.isLoading ? (
            <SkeletonText lines={6} />
          ) : messages.data?.items.length ? (
            <ol className="message-list">
              {messages.data.items.map((message) => {
                const outbound = message.direction === "outbound";
                const author = outbound ? message.authorName || "Automatic" : thread.customerName;
                const body = splitQuoted(message.textBody);
                // A reply's subject is almost always the thread's subject wearing another "Re:",
                // which the page header has already said once. Only a subject that actually
                // changed carries information worth a line of its own.
                const freshSubject =
                  message.subject !== "" &&
                  normalizeSubject(message.subject) !== normalizeSubject(thread.subject);
                return (
                  <li key={message.id} className={`message message-${message.direction}`}>
                    <div className="message-head">
                      <span className="message-avatar" aria-hidden="true">
                        {initials(author)}
                      </span>
                      <div className="message-who">
                        <strong>{author}</strong>
                        <small className="muted">
                          {outbound ? `to ${message.address}` : `from ${message.address}`}
                        </small>
                      </div>
                      <div className="message-when">
                        <small className="muted">
                          {formatDateTime(message.occurredAt)}
                          {message.scheduledAt
                            ? `, due ${formatDateTime(message.scheduledAt)}`
                            : message.sentAt
                              ? `, sent ${formatDateTime(message.sentAt)}`
                              : ""}
                        </small>
                        <StatusPill
                          value={message.status}
                          label={messageStatusLabels[message.status]}
                        />
                        {outbound && message.status === "scheduled" ? (
                          // Stopping a send had a handler, a confirm dialog, wording and a route on
                          // both sides, and no control that opened any of it: setCancelling was only
                          // ever called with null, so the dialog could not open and a scheduled
                          // message could not be cancelled from the conversation it belongs to.
                          <button
                            className="button ghost small"
                            type="button"
                            disabled={working}
                            onClick={() => setCancelling(message)}
                          >
                            <Ban size={14} aria-hidden="true" />
                            Cancel send
                          </button>
                        ) : null}
                        {outbound && message.status === "failed" ? (
                          // Failure is the one outbound state where nothing reached the customer, so
                          // it is the only one where sending again cannot put a second copy of the
                          // same message in front of them. Anything still queued or sending is left
                          // alone precisely because it may yet arrive.
                          <button
                            className="button ghost small"
                            type="button"
                            disabled={working}
                            onClick={() => retrySend(message)}
                          >
                            <RefreshCw size={14} aria-hidden="true" />
                            Retry send
                          </button>
                        ) : null}
                      </div>
                    </div>
                    {freshSubject ? <p className="message-subject">{message.subject}</p> : null}
                    {body.reply ? <p className="message-body">{body.reply}</p> : null}
                    {body.quoted ? (
                      // The history every mail client pastes under a reply, folded so the third
                      // message in a thread does not repeat the first two at full weight.
                      <details className="message-quote">
                        <summary>Show the earlier messages quoted in this reply</summary>
                        <p className="message-body message-quoted">{body.quoted}</p>
                      </details>
                    ) : null}
                    {message.htmlBody ? (
                      <details className="message-html-toggle" open={message.textBody === ""}>
                        <summary>
                          {message.textBody === "" ? "Formatted message" : "Show formatted message"}
                        </summary>
                        {/*
                          Inbound email HTML is attacker controlled: anybody who can send this venue
                          a message chooses it. So it is handed to an iframe rather than injected
                          into this document, and `sandbox` with no tokens is the most restrictive
                          form there is: opaque origin, no scripts, no forms, no navigation, no
                          reach into this page or its session. That holds on its own, without
                          depending on the Content-Security-Policy, which is the second control.
                        */}
                        <iframe
                          className="message-html"
                          title={`Formatted version of: ${message.subject || "no subject"}`}
                          sandbox=""
                          referrerPolicy="no-referrer"
                          loading="lazy"
                          srcDoc={message.htmlBody}
                        />
                      </details>
                    ) : null}
                    {message.attachments?.length ? (
                      <ul className="attachment-list">
                        {message.attachments.map((att) => (
                          <li key={att.id} className="attachment-item">
                            <Paperclip size={14} aria-hidden="true" />
                            <a
                              href={attachmentDownloadUrl(thread.id, att.id)}
                              download={att.filename}
                              className="button ghost small"
                            >
                              <Download size={14} aria-hidden="true" />
                              {att.filename}
                            </a>
                            <small className="muted">{formatFileSize(att.sizeBytes)}</small>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    {message.lastError ? (
                      <small className="field-error">
                        {messageStatusLabels[message.status] ?? message.status}: {message.lastError}
                      </small>
                    ) : null}
                  </li>
                );
              })}
            </ol>
          ) : (
            <EmptyState
              title="Nothing here yet"
              description="The first message in this conversation has not been written."
            />
          )}
        </Card>
        <Card>
          <CardHeader
            title="Reply"
            description={
              thread.status === "open"
                ? "Goes to the same customer this conversation is with."
                : "Reopen the conversation before replying, so the exchange stays in one place."
            }
          />
          <form className="form-grid" onSubmit={reply}>
            <MessageFields
              templates={available}
              draft={draft}
              onChange={setDraft}
              disabled={thread.status !== "open"}
            />
            <div className="full">
              <FormActions
                submitting={working}
                disabled={thread.status !== "open"}
                submitLabel={draft.scheduledAt ? "Schedule reply" : "Send reply"}
              />
            </div>
          </form>
        </Card>
      </div>
      <ConfirmDialog
        open={Boolean(cancelling)}
        title="Cancel this scheduled message?"
        description="The message stays in the conversation marked as canceled, because somebody stopping a send is itself part of the history of what was communicated."
        confirmLabel="Cancel the send"
        submitting={working}
        onClose={() => setCancelling(null)}
        onConfirm={cancelScheduled}
      />
    </EditorPage>
  );
}
